import i18n from "../i18n";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { formatCurrency } from "@/lib/format";
import { FARM_TIMEZONE, farmCalendarParts, farmDateIso, farmDateRangeForPreset } from "@/lib/farmTime";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { Clock3, Download, FileSpreadsheet, ShieldCheck, Users, Wallet } from "lucide-react";

const LIVE_STATUSES = [
    { value: "", label: "All Statuses" },
    { value: "not_started", label: "Not Started" },
    { value: "shift_started", label: "Shift Started" },
    { value: "working", label: "Working" },
    { value: "on_break", label: "On Break" },
    { value: "clocked_out", label: "Clocked Out" },
    { value: "finished", label: "Finished" },
    { value: "late", label: "Late" },
    { value: "absent", label: "Absent" },
];

const LOG_STATUS = ["all", "working", "on_break", "clocked_out", "finished", "late", "absent"];
const OVERTIME_STATUS = ["all", "pending", "approved", "adjusted", "rejected", "paid"];
const LEAVE_STATUS = ["all", "pending", "approved", "rejected"];
const LEAVE_TYPES = ["annual", "sick", "emergency", "other"];
const ATTENDANCE_TABS = new Set(["live", "logs", "calendar", "timesheets", "overtime", "leaves", "reports"]);
function isoToday() {
    return farmDateIso();
}

function hhmmFromIso(value) {
    if (!value) return "-";
    try {
        if (/^\d{2}:\d{2}(:\d{2})?$/.test(String(value))) {
            return String(value).slice(0, 5);
        }
        const d = new Date(value);
        if (Number.isNaN(d.getTime())) return "-";
        return d.toLocaleTimeString("en-PH", {
            timeZone: FARM_TIMEZONE,
            hour: "2-digit",
            minute: "2-digit",
            hour12: false,
        });
    } catch {
        return "-";
    }
}

function hoursLabel(value) {
    const n = Number(value || 0);
    return `${n.toFixed(2)}h`;
}

function statusLabel(value) {
    const key = String(value || "").replace(/_/g, " ");
    return key ? key.charAt(0).toUpperCase() + key.slice(1) : "-";
}

export default function AttendancePayrollPage() {
    const isDevMode = process.env.NODE_ENV !== "production";
    const [params, setParams] = useSearchParams();
    const nav = useNavigate();

    const initialTab = ATTENDANCE_TABS.has(params.get("tab") || "") ? (params.get("tab") || "live") : "live";
    const [tab, setTab] = useState(initialTab);

    const [settings, setSettings] = useState(null);

    const [liveDate, setLiveDate] = useState(params.get("date") || isoToday());
    const [liveQ, setLiveQ] = useState(params.get("q") || "");
    const [liveDept, setLiveDept] = useState(params.get("department") || "");
    const [liveStatus, setLiveStatus] = useState(params.get("status") || "");
    const [liveRows, setLiveRows] = useState([]);
    const [liveSummary, setLiveSummary] = useState({});

    const [logPreset, setLogPreset] = useState("today");
    const [logFrom, setLogFrom] = useState(farmDateRangeForPreset("today").from);
    const [logTo, setLogTo] = useState(farmDateRangeForPreset("today").to);
    const [logStatus, setLogStatus] = useState("all");
    const [logRows, setLogRows] = useState([]);

    const farmNow = farmCalendarParts();
    const [sheetYear, setSheetYear] = useState(Number(params.get("year") || farmNow.year));
    const [sheetMonth, setSheetMonth] = useState(Number(params.get("month") || farmNow.month));
    const [sheetPeriod, setSheetPeriod] = useState("monthly");
    const [sheetFrom, setSheetFrom] = useState(farmDateRangeForPreset("month").from);
    const [sheetTo, setSheetTo] = useState(farmDateRangeForPreset("month").to);
    const [timesheets, setTimesheets] = useState([]);
    const [timesheetEntries, setTimesheetEntries] = useState(null);

    const [otYear, setOtYear] = useState(Number(params.get("year") || farmNow.year));
    const [otMonth, setOtMonth] = useState(Number(params.get("month") || farmNow.month));
    const [otStatus, setOtStatus] = useState("all");
    const [overtimeRows, setOvertimeRows] = useState([]);
    const [overtimeSummary, setOvertimeSummary] = useState({});

    const [leaveYear, setLeaveYear] = useState(Number(params.get("year") || farmNow.year));
    const [leaveStatus, setLeaveStatus] = useState("all");
    const [leaveType, setLeaveType] = useState("all");
    const [leaveRows, setLeaveRows] = useState([]);
    const [leaveBalance, setLeaveBalance] = useState(null);
    const [leaveEditingId, setLeaveEditingId] = useState("");
    const [leaveForm, setLeaveForm] = useState({
        employee_id: "",
        leave_type: "annual",
        start_date: isoToday(),
        end_date: isoToday(),
        reason: "",
        notes: "",
    });

    const [employeeRows, setEmployeeRows] = useState([]);
    const [reportType, setReportType] = useState("attendance_summary");
    const [reportRows, setReportRows] = useState([]);
    const [reportSummary, setReportSummary] = useState({});

    const [detailOpen, setDetailOpen] = useState(false);
    const [detailRow, setDetailRow] = useState(null);

    const [adjustOpen, setAdjustOpen] = useState(false);
    const [adjustTarget, setAdjustTarget] = useState(null);
    const [adjustForm, setAdjustForm] = useState({
        clock_in: "",
        break_start: "",
        break_end: "",
        clock_out: "",
        shift_end: "",
        status: "",
        notes: "",
        reason: "",
    });

    const loadSettings = useCallback(async () => {
        try {
            const { data } = await api.get("/attendance/settings");
            setSettings(data || null);
        } catch {
            setSettings(null);
        }
    }, []);

    const loadLive = useCallback(async () => {
        try {
            const { data } = await api.get("/attendance/live", {
                params: {
                    date: liveDate,
                    q: liveQ || undefined,
                    department: liveDept || undefined,
                    status: liveStatus || undefined,
                    employee_id: params.get("employee_id") || undefined,
                },
            });
            setLiveRows(Array.isArray(data?.rows) ? data.rows : []);
            setLiveSummary(data?.summary || {});
            if (data?.settings) setSettings(data.settings);
        } catch (err) {
            toast.error(err.response?.data?.detail || "Failed to load live attendance");
            setLiveRows([]);
            setLiveSummary({});
        }
    }, [liveDate, liveQ, liveDept, liveStatus, params]);

    const loadLogs = useCallback(async () => {
        try {
            const { data } = await api.get("/attendance/logs", {
                params: {
                    from: logFrom,
                    to: logTo,
                    status: logStatus === "all" ? undefined : logStatus,
                    employee_id: params.get("employee_id") || undefined,
                },
            });
            setLogRows(Array.isArray(data?.rows) ? data.rows : []);
        } catch (err) {
            toast.error(err.response?.data?.detail || "Failed to load logs");
            setLogRows([]);
        }
    }, [logFrom, logTo, logStatus, params]);

    const loadTimesheets = useCallback(async () => {
        try {
            const { data } = await api.get("/attendance/timesheets", {
                params: {
                    year: sheetYear,
                    month: sheetMonth,
                    period: sheetPeriod,
                    from: sheetPeriod === "custom" ? sheetFrom : undefined,
                    to: sheetPeriod === "custom" ? sheetTo : undefined,
                    employee_id: params.get("employee_id") || undefined,
                },
            });
            setTimesheets(Array.isArray(data?.rows) ? data.rows : []);
        } catch (err) {
            toast.error(err.response?.data?.detail || "Failed to load timesheets");
            setTimesheets([]);
        }
    }, [sheetYear, sheetMonth, sheetPeriod, sheetFrom, sheetTo, params]);

    const loadEmployees = useCallback(async () => {
        try {
            const { data } = await api.get("/employees");
            setEmployeeRows(Array.isArray(data) ? data : []);
        } catch {
            setEmployeeRows([]);
        }
    }, []);

    const loadReports = useCallback(async () => {
        try {
            const { data } = await api.get("/attendance/reports", {
                params: {
                    report_type: reportType,
                    year: sheetYear,
                    month: sheetMonth,
                    from: logFrom,
                    to: logTo,
                    employee_id: params.get("employee_id") || undefined,
                },
            });
            setReportRows(Array.isArray(data?.rows) ? data.rows : []);
            setReportSummary(data?.summary || {});
        } catch (err) {
            toast.error(err.response?.data?.detail || "Failed to load attendance reports");
            setReportRows([]);
            setReportSummary({});
        }
    }, [reportType, sheetYear, sheetMonth, logFrom, logTo, params]);

    const loadOvertime = useCallback(async () => {
        try {
            const { data } = await api.get("/attendance/overtime", {
                params: {
                    year: otYear,
                    month: otMonth,
                    status: otStatus === "all" ? undefined : otStatus,
                    employee_id: params.get("employee_id") || undefined,
                },
            });
            setOvertimeRows(Array.isArray(data?.rows) ? data.rows : []);
            setOvertimeSummary(data?.summary || {});
            if (data?.settings) setSettings(data.settings);
        } catch (err) {
            toast.error(err.response?.data?.detail || "Failed to load overtime requests");
            setOvertimeRows([]);
            setOvertimeSummary({});
        }
    }, [otYear, otMonth, otStatus, params]);

    const loadLeaves = useCallback(async () => {
        try {
            const employeeId = params.get("employee_id") || undefined;
            const { data } = await api.get("/leaves", {
                params: {
                    year: leaveYear,
                    status: leaveStatus === "all" ? undefined : leaveStatus,
                    leave_type: leaveType === "all" ? undefined : leaveType,
                    employee_id: employeeId,
                },
            });
            setLeaveRows(Array.isArray(data) ? data : []);
        } catch (err) {
            toast.error(err.response?.data?.detail || "Failed to load leave requests");
            setLeaveRows([]);
        }

        try {
            const employeeId = params.get("employee_id") || "";
            const endpoint = employeeId ? `/leaves/balance/${employeeId}` : "/leaves/balance";
            const { data } = await api.get(endpoint, { params: { year: leaveYear } });
            setLeaveBalance(data || null);
        } catch {
            setLeaveBalance(null);
        }
    }, [leaveYear, leaveStatus, leaveType, params]);

    useEffect(() => {
        loadSettings();
    }, [loadSettings]);

    useEffect(() => {
        if (tab === "live") loadLive();
        if (tab === "logs") loadLogs();
        if (tab === "calendar") loadEmployees();
        if (tab === "timesheets") loadTimesheets();
        if (tab === "overtime") loadOvertime();
        if (tab === "leaves") loadLeaves();
        if (tab === "reports") loadReports();
    }, [tab, loadLive, loadLogs, loadEmployees, loadTimesheets, loadOvertime, loadLeaves, loadReports]);

    useEffect(() => {
        if (tab !== "live") return undefined;
        const t = setInterval(() => {
            loadLive();
        }, 30000);
        return () => clearInterval(t);
    }, [tab, loadLive]);

    useEffect(() => {
        setParams((old) => {
            const next = new URLSearchParams(old);
            next.set("tab", tab);
            next.set("date", liveDate);
            next.set("year", String(sheetYear));
            next.set("month", String(sheetMonth));
            return next;
        });
    }, [tab, liveDate, sheetYear, sheetMonth, setParams]);

    const departments = useMemo(() => {
        const set = new Set((liveRows || []).map((row) => String(row.department || "").trim()).filter(Boolean));
        return Array.from(set).sort((a, b) => a.localeCompare(b));
    }, [liveRows]);

    const callAttendanceAction = async (id, action, body = {}) => {
        try {
            await api.put(`/attendance/${id}/${action}`, body);
            toast.success(`Record ${action}d`);
            await Promise.all([loadLive(), loadLogs(), loadTimesheets(), loadOvertime()]);
            if (detailRow?.id === id) {
                const { data } = await api.get(`/attendance/${id}`);
                setDetailRow(data);
            }
        } catch (err) {
            toast.error(err.response?.data?.detail || `Unable to ${action} record`);
        }
    };

    const openDetail = async (row) => {
        try {
            const { data } = await api.get(`/attendance/${row.id}`);
            setDetailRow(data);
            setDetailOpen(true);
        } catch (err) {
            toast.error(err.response?.data?.detail || "Unable to open attendance detail");
        }
    };

    const resetTodayAttendance = async (row) => {
        if (!isDevMode) return;
        if (!row?.employee_id) return;
        const ok = window.confirm(`Reset today's attendance for ${row.employee_name || "this employee"}? This only affects today.`);
        if (!ok) return;
        try {
            await api.post("/attendance/reset-today", { employee_id: row.employee_id });
            toast.success("Today's attendance was reset");
            await Promise.all([loadLive(), loadLogs(), loadTimesheets(), loadOvertime()]);
        } catch (err) {
            toast.error(err.response?.data?.detail || "Failed to reset attendance");
        }
    };

    const callOvertimeAction = async (id, action, body = {}) => {
        try {
            await api.put(`/attendance/overtime/${id}/${action}`, body);
            toast.success(`Overtime ${action}d`);
            await Promise.all([loadOvertime(), loadTimesheets()]);
        } catch (err) {
            toast.error(err.response?.data?.detail || `Unable to ${action} overtime request`);
        }
    };

    const submitLeave = async () => {
        if (!leaveForm.start_date || !leaveForm.end_date) {
            toast.error("Start and end dates are required");
            return;
        }
        const payload = {
            employee_id: leaveForm.employee_id || undefined,
            leave_type: leaveForm.leave_type,
            start_date: leaveForm.start_date,
            end_date: leaveForm.end_date,
            reason: leaveForm.reason,
            notes: leaveForm.notes,
        };
        try {
            if (leaveEditingId) {
                await api.put(`/leaves/${leaveEditingId}`, payload);
                toast.success("Leave request updated");
            } else {
                await api.post("/leaves", payload);
                toast.success("Leave request submitted");
            }
            setLeaveEditingId("");
            setLeaveForm((prev) => ({ ...prev, reason: "", notes: "" }));
            await loadLeaves();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Failed to submit leave request");
        }
    };

    const editLeave = (row) => {
        setLeaveEditingId(row.id || "");
        setLeaveForm({
            employee_id: row.employee_id || "",
            leave_type: row.leave_type || "annual",
            start_date: row.start_date || isoToday(),
            end_date: row.end_date || isoToday(),
            reason: row.reason || "",
            notes: row.notes || "",
        });
    };

    const setLeaveStatusAction = async (id, action) => {
        try {
            if (action === "approve") {
                const note = window.prompt("Approval note (optional)") || "";
                await api.put(`/leaves/${id}/approve`, { note });
                toast.success("Leave approved");
            } else {
                const reason = window.prompt("Rejection reason");
                if (!reason) return;
                await api.put(`/leaves/${id}/reject`, { reason });
                toast.success("Leave rejected");
            }
            await loadLeaves();
        } catch (err) {
            toast.error(err.response?.data?.detail || `Failed to ${action} leave request`);
        }
    };

    const removeLeave = async (id) => {
        const ok = window.confirm("Delete this leave request?");
        if (!ok) return;
        try {
            await api.delete(`/leaves/${id}`);
            toast.success("Leave request deleted");
            await loadLeaves();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Failed to delete leave request");
        }
    };

    const openAdjust = (row) => {
        setAdjustTarget(row);
        setAdjustForm({
            clock_in: row.clock_in || "",
            break_start: row.break_start || "",
            break_end: row.break_end || "",
            clock_out: row.clock_out || "",
            shift_end: row.shift_end || "",
            status: row.status || "",
            notes: row.notes || "",
            reason: "",
        });
        setAdjustOpen(true);
    };

    const saveAdjust = async () => {
        if (!adjustTarget?.id) return;
        if (!adjustForm.reason.trim()) {
            toast.error("Adjustment reason is required");
            return;
        }
        await callAttendanceAction(adjustTarget.id, "adjust", {
            clock_in: adjustForm.clock_in || null,
            break_start: adjustForm.break_start || null,
            break_end: adjustForm.break_end || null,
            clock_out: adjustForm.clock_out || null,
            shift_end: adjustForm.shift_end || null,
            status: adjustForm.status || null,
            notes: adjustForm.notes || "",
            reason: adjustForm.reason.trim(),
        });
        setAdjustOpen(false);
    };

    const exportRowsCsv = (rows, filename) => {
        if (!Array.isArray(rows) || rows.length === 0) {
            toast.error("No rows to export");
            return;
        }
        const headers = Object.keys(rows[0]);
        const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
        const csv = [headers.join(","), ...rows.map((row) => headers.map((h) => esc(row[h])).join(","))].join("\n");
        const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
    };

    const exportLogsExcel = async () => {
        const XLSX = await import("xlsx");
        const rows = (logRows || []).map((row) => ({
            date: row.date,
            employee: row.employee_name,
            employee_code: row.employee_code,
            shift_start: row.shift_start || "",
            clock_in: row.clock_in || row.check_in_time || "",
            break_start: row.break_start || "",
            break_end: row.break_end || "",
            clock_out: row.clock_out || row.check_out_time || "",
            shift_end: row.shift_end || "",
            regular_hours: row.regular_hours,
            break_hours: row.break_hours,
            overtime_hours: row.overtime_hours,
            late_minutes: row.late_minutes,
            status: row.status,
            approval_status: row.approval_status,
        }));
        const wb = XLSX.utils.book_new();
        const ws = XLSX.utils.json_to_sheet(rows);
        XLSX.utils.book_append_sheet(wb, ws, "Daily Logs");
        const buf = XLSX.write(wb, { type: "array", bookType: "xlsx" });
        const blob = new Blob([buf], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `attendance-daily-logs-${logFrom}-to-${logTo}.xlsx`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
    };

    const exportTimesheetExcel = async () => {
        const XLSX = await import("xlsx");
        const rows = (timesheets || []).map((row) => ({
            employee: row.employee_name,
            employee_code: row.employee_code,
            days_present: row.days_present,
            days_late: row.days_late,
            days_absent: row.days_absent,
            regular_hours: row.regular_hours,
            break_hours: row.break_hours,
            approved_overtime_hours: row.overtime_hours,
            pending_overtime_hours: row.pending_overtime_hours,
            overtime_pay: row.overtime_pay,
            approved_days: row.approved_days,
            pending_days: row.pending_days,
            estimated_gross_pay: row.estimated_gross_pay,
        }));
        const wb = XLSX.utils.book_new();
        const ws = XLSX.utils.json_to_sheet(rows);
        XLSX.utils.book_append_sheet(wb, ws, "Timesheets");
        const buf = XLSX.write(wb, { type: "array", bookType: "xlsx" });
        const blob = new Blob([buf], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `attendance-timesheet-${sheetYear}-${String(sheetMonth).padStart(2, "0")}.xlsx`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
    };

    const exportTimesheetPdf = () => {
        const doc = new jsPDF({ orientation: "landscape" });
        doc.setFontSize(14);
        doc.text("Attendance Timesheet", 14, 16);
        doc.setFontSize(10);
        doc.text(`Period: ${sheetPeriod} | ${sheetYear}-${String(sheetMonth).padStart(2, "0")}`, 14, 24);
        const body = (timesheets || []).map((row) => [
            row.employee_name || "-",
            row.days_present || 0,
            row.days_late || 0,
            row.days_absent || 0,
            hoursLabel(row.regular_hours),
            hoursLabel(row.overtime_hours),
            row.estimated_gross_pay == null ? "Rate not configured" : formatCurrency(row.estimated_gross_pay, settings?.payroll_currency || "PHP"),
        ]);
        autoTable(doc, {
            startY: 30,
            head: [["Employee", "Present", "Late", "Absent", "Regular Hrs", "OT Hrs", "Est Gross"]],
            body,
            styles: { fontSize: 8 },
            headStyles: { fillColor: [46, 125, 50] },
        });
        doc.save(`attendance-timesheet-${sheetYear}-${String(sheetMonth).padStart(2, "0")}.pdf`);
    };

    const exportOvertimeExcel = async () => {
        const XLSX = await import("xlsx");
        const rows = (overtimeRows || []).map((row) => ({
            date: row.date,
            employee: row.employee_name,
            employee_code: row.employee_code,
            department: row.department,
            requested_overtime_hours: row.requested_overtime_hours,
            approved_overtime_hours: row.approved_overtime_hours,
            status: row.approval_status,
            overtime_rate: row.overtime_rate,
            estimated_overtime_pay: row.estimated_overtime_pay,
            scheduled_shift_end: row.scheduled_shift_end_display || row.scheduled_shift_end,
            actual_clock_out: row.actual_clock_out_display || row.actual_clock_out,
            overtime_start: row.overtime_start_display || row.overtime_start,
            overtime_end: row.overtime_end_display || row.overtime_end,
            requested_at: row.requested_at_display || row.requested_at,
            approved_at: row.approved_at_display || row.approved_at,
            reason: row.reason,
            related_subject: row.related_subject,
            supervisor: row.supervisor,
        }));
        const wb = XLSX.utils.book_new();
        const ws = XLSX.utils.json_to_sheet(rows);
        XLSX.utils.book_append_sheet(wb, ws, "Overtime");
        const buf = XLSX.write(wb, { type: "array", bookType: "xlsx" });
        const blob = new Blob([buf], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `attendance-overtime-${otYear}-${String(otMonth).padStart(2, "0")}.xlsx`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
    };

    return (
        <div className="space-y-5" data-testid="attendance-page">
            <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                <div>
                    <div className="tiny-label">{i18n.t("batch1.89e7a04b9eda")}</div>
                    <h1 className="text-3xl font-bold tracking-tight">{i18n.t("batch1.7dd2bff06693")}</h1>
                    <p className="text-sm text-muted-foreground">{i18n.t("batch1.6c4f11cc0cc7")}</p>
                </div>
                <Button variant="outline" className="h-11" onClick={() => nav("/employees")}>
                    <Users className="w-4 h-4 mr-2" /> {i18n.t("batch1.b85b0d2563b0")}
                </Button>
            </div>

            <Tabs value={tab} onValueChange={setTab}>
                <TabsList className="h-auto flex flex-wrap" data-testid="attendance-tabs">
                    <TabsTrigger value="live" className="h-10">{i18n.t("batch1.b50da595e41b")}</TabsTrigger>
                    <TabsTrigger value="logs" className="h-10">{i18n.t("batch1.5393bd8aa99e")}</TabsTrigger>
                    <TabsTrigger value="calendar" className="h-10">{i18n.t("batch1.346714c46c07")}</TabsTrigger>
                    <TabsTrigger value="timesheets" className="h-10">{i18n.t("batch1.1b485b9e171b")}</TabsTrigger>
                    <TabsTrigger value="overtime" className="h-10">{i18n.t("batch1.4a8dbfe40169")}</TabsTrigger>
                    <TabsTrigger value="leaves" className="h-10">{i18n.t("batch1.d49aff000867")}</TabsTrigger>
                    <TabsTrigger value="reports" className="h-10">{i18n.t("batch1.3197cbcce5dd")}</TabsTrigger>
                </TabsList>

                <TabsContent value="live" className="space-y-4">
                    <Card className="p-4">
                        {isDevMode ? (
                            <div className="mb-3 rounded-md border border-amber-400/40 bg-amber-50 px-3 py-2 text-xs text-amber-900">
                                {i18n.t("batch1.34115ad627a6")}
                            </div>
                        ) : null}
                        <div className="grid grid-cols-1 md:grid-cols-6 gap-3">
                            <Input type="date" value={liveDate} onChange={(e) => setLiveDate(e.target.value)} className="h-11" data-testid="live-date" />
                            <Input placeholder={i18n.t("batch1.1c9a7a0c6e70")} value={liveQ} onChange={(e) => setLiveQ(e.target.value)} className="h-11" data-testid="live-search" />
                            <Select value={liveDept || "__all__"} onValueChange={(v) => setLiveDept(v === "__all__" ? "" : v)}>
                                <SelectTrigger className="h-11" data-testid="live-department"><SelectValue placeholder={i18n.t("batch1.db40106a4051")} /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="__all__">{i18n.t("batch1.f66d88d1c09a")}</SelectItem>
                                    {departments.map((dep) => <SelectItem key={dep} value={dep}>{dep}</SelectItem>)}
                                </SelectContent>
                            </Select>
                            <Select value={liveStatus || "__all__"} onValueChange={(v) => setLiveStatus(v === "__all__" ? "" : v)}>
                                <SelectTrigger className="h-11" data-testid="live-status"><SelectValue placeholder={i18n.t("batch1.bae7d5be7082")} /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="__all__">{i18n.t("batch1.9cb29e734a6c")}</SelectItem>
                                    {LIVE_STATUSES.filter((s) => s.value).map((st) => <SelectItem key={st.value} value={st.value}>{st.label}</SelectItem>)}
                                </SelectContent>
                            </Select>
                            <Button className="h-11" variant="outline" onClick={() => setLiveDate(isoToday())}>{i18n.t("batch1.24345a14377f")}</Button>
                            <Button className="h-11" onClick={loadLive}>{i18n.t("batch1.56e3badc4e6c")}</Button>
                        </div>
                    </Card>

                    <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-3">
                        <SummaryCard icon={Users} label={i18n.t("batch1.07fcdbd896a2")} value={liveSummary.total_employees || 0} />
                        <SummaryCard icon={Clock3} label={i18n.t("batch1.05882967d7f8")} value={liveSummary.present_today || 0} tone="primary" />
                        <SummaryCard icon={Clock3} label={i18n.t("batch1.08189b0386c8")} value={liveSummary.working_now || 0} tone="primary" />
                        <SummaryCard icon={Clock3} label={i18n.t("batch1.4317a9f49f13")} value={liveSummary.on_break || 0} />
                        <SummaryCard icon={Clock3} label={i18n.t("batch1.355bcc577d71")} value={liveSummary.finished || 0} />
                        <SummaryCard icon={Clock3} label={i18n.t("batch1.4310ed540c1c")} value={liveSummary.late || 0} tone="amber" />
                        <SummaryCard icon={Clock3} label={i18n.t("batch1.e92452c8d3e2")} value={liveSummary.absent || 0} tone="red" />
                        <SummaryCard icon={ShieldCheck} label={i18n.t("batch1.7ba0a4f030d1")} value={liveSummary.pending_approval || 0} />
                    </div>

                    <Card className="p-0 overflow-x-auto" data-testid="live-table-card">
                        <table className="w-full text-sm">
                            <thead className="bg-secondary/60 text-xs uppercase text-muted-foreground">
                                <tr>
                                    <Th>{i18n.t("batch1.079711ea16f3")}</Th><Th>{i18n.t("batch1.adac69379a62")}</Th><Th>{i18n.t("batch1.db40106a4051")}</Th><Th>{i18n.t("batch1.a65de8bce998")}</Th><Th>{i18n.t("batch1.bae7d5be7082")}</Th>
                                    <Th>{i18n.t("batch1.c36f953ce513")}</Th><Th>{i18n.t("batch1.8d3e921408c8")}</Th><Th>{i18n.t("batch1.31e6ef3947c6")}</Th><Th>{i18n.t("batch1.f81283ceb61e")}</Th><Th>{i18n.t("batch1.7f9bf082bf6a")}</Th><Th>{i18n.t("batch1.b4ebd03d7de2")}</Th>
                                    <Th>{i18n.t("batch1.55bd39660c34")}</Th><Th>{i18n.t("batch1.ba4adca50018")}</Th><Th>{i18n.t("batch1.455277469acd")}</Th><Th>{i18n.t("batch1.da76a7170f8f")}</Th><Th>{i18n.t("batch1.1776fa32f23e")}</Th><Th>{i18n.t("batch1.a5a74a6df092")}</Th><Th>{i18n.t("batch1.d01d900383e4")}</Th><Th>{i18n.t("batch1.905f63092ff2")}</Th><Th>{i18n.t("batch1.97c89a4d6630")}</Th>
                                </tr>
                            </thead>
                            <tbody>
                                {liveRows.map((row) => (
                                    <tr key={row.id} className="border-t border-border/50">
                                        <Td>{row.employee_name || "-"}</Td>
                                        <Td>{row.employee_code || "-"}</Td>
                                        <Td>{row.department || "-"}</Td>
                                        <Td>{row.assigned_area || "-"}</Td>
                                        <Td>{statusLabel(row.status)}</Td>
                                        <Td>{hhmmFromIso(row.shift_start)}</Td>
                                        <Td>{hhmmFromIso(row.clock_in || row.check_in_time)}</Td>
                                        <Td>{hhmmFromIso(row.break_start)}</Td>
                                        <Td>{hhmmFromIso(row.break_end)}</Td>
                                        <Td>{hhmmFromIso(row.clock_out || row.check_out_time)}</Td>
                                        <Td>{hhmmFromIso(row.shift_end)}</Td>
                                        <Td>{hoursLabel(row.worked_hours)}</Td>
                                        <Td>{Number(row.late_minutes || 0)}</Td>
                                        <Td>{Number(row.early_leave_minutes || 0)}</Td>
                                        <Td>{hoursLabel(row.overtime_hours || 0)}</Td>
                                        <Td>{row.gps || "-"}</Td>
                                        <Td>{row.device || "-"}</Td>
                                        <Td>{row.photo_verification && row.photo_verification !== "-" ? "Verified" : "-"}</Td>
                                        <Td>{statusLabel(row.sync_status || "synced")}</Td>
                                        <Td>
                                            <div className="flex gap-1">
                                                <Button size="sm" variant="outline" onClick={() => openDetail(row)}>{i18n.t("batch1.69bd4ef9fbd0")}</Button>
                                                <Button
                                                    size="sm"
                                                    variant="outline"
                                                    onClick={() => nav(`/attendance/calendar?employee_id=${encodeURIComponent(row.employee_id)}&year=${sheetYear}&month=${sheetMonth}`)}
                                                >
                                                    {i18n.t("batch1.957f629f801e")}
                                                </Button>
                                                {isDevMode ? (
                                                    <Button
                                                        size="sm"
                                                        variant="outline"
                                                        className="text-amber-700 border-amber-300"
                                                        onClick={() => resetTodayAttendance(row)}
                                                        data-testid={`reset-today-${row.employee_id || row.id}`}
                                                    >
                                                        {i18n.t("batch1.ce83e89cd164")}
                                                    </Button>
                                                ) : null}
                                            </div>
                                        </Td>
                                    </tr>
                                ))}
                                {liveRows.length === 0 && <tr><Td colSpan={20} className="text-center py-8 text-muted-foreground">{i18n.t("batch1.156ce256cf91")}</Td></tr>}
                            </tbody>
                        </table>
                    </Card>
                </TabsContent>

                <TabsContent value="logs" className="space-y-4">
                    <Card className="p-4">
                        <div className="flex flex-wrap gap-2 mb-3">
                            {[
                                ["today", "Today"],
                                ["yesterday", "Yesterday"],
                                ["week", "This Week"],
                                ["month", "This Month"],
                            ].map(([value, label]) => (
                                <Button
                                    key={value}
                                    variant={logPreset === value ? "default" : "outline"}
                                    className="h-9"
                                    onClick={() => {
                                        setLogPreset(value);
                                        const range = farmDateRangeForPreset(value);
                                        setLogFrom(range.from);
                                        setLogTo(range.to);
                                    }}
                                >{label}</Button>
                            ))}
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
                            <Input type="date" value={logFrom} onChange={(e) => { setLogPreset("custom"); setLogFrom(e.target.value); }} className="h-11" />
                            <Input type="date" value={logTo} onChange={(e) => { setLogPreset("custom"); setLogTo(e.target.value); }} className="h-11" />
                            <Select value={logStatus} onValueChange={setLogStatus}>
                                <SelectTrigger className="h-11"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    {LOG_STATUS.map((st) => <SelectItem key={st} value={st}>{st === "all" ? "All Statuses" : statusLabel(st)}</SelectItem>)}
                                </SelectContent>
                            </Select>
                            <Button className="h-11" onClick={loadLogs}>{i18n.t("batch1.cfea419c3b4e")}</Button>
                            <Button className="h-11" variant="outline" onClick={exportLogsExcel}><FileSpreadsheet className="w-4 h-4 mr-2" />{i18n.t("batch1.4a245944efb6")}</Button>
                        </div>
                    </Card>

                    <Card className="p-0 overflow-x-auto" data-testid="daily-logs-table">
                        <table className="w-full text-sm">
                            <thead className="bg-secondary/60 text-xs uppercase text-muted-foreground">
                                <tr>
                                    <Th>{i18n.t("batch1.eb9a4bc1c0c1")}</Th><Th>{i18n.t("batch1.079711ea16f3")}</Th><Th>{i18n.t("batch1.c36f953ce513")}</Th><Th>{i18n.t("batch1.8d3e921408c8")}</Th><Th>{i18n.t("batch1.31e6ef3947c6")}</Th><Th>{i18n.t("batch1.f81283ceb61e")}</Th><Th>{i18n.t("batch1.7f9bf082bf6a")}</Th><Th>{i18n.t("batch1.b4ebd03d7de2")}</Th>
                                    <Th>{i18n.t("batch1.777800dce2f6")}</Th><Th>{i18n.t("batch1.3b3d106c9d41")}</Th><Th>{i18n.t("batch1.a8231c45a592")}</Th><Th>{i18n.t("batch1.cb880d37b1d5")}</Th><Th>{i18n.t("batch1.ba4adca50018")}</Th><Th>{i18n.t("batch1.bae7d5be7082")}</Th><Th>{i18n.t("batch1.8cc047ac17d3")}</Th>
                                    <Th>{i18n.t("batch1.1776fa32f23e")}</Th><Th>{i18n.t("batch1.d219c68101f5")}</Th><Th>{i18n.t("batch1.d01d900383e4")}</Th><Th>{i18n.t("batch1.a5a74a6df092")}</Th><Th>{i18n.t("batch1.56f04191af49")}</Th><Th>{i18n.t("batch1.34b9ee32d987")}</Th><Th>{i18n.t("batch1.3703cd216881")}</Th><Th>{i18n.t("batch1.4ec763aab4f8")}</Th><Th>{i18n.t("batch1.36155d48f460")}</Th><Th>{i18n.t("batch1.5e326b17d2aa")}</Th><Th>{i18n.t("batch1.5124fc39013f")}</Th><Th>{i18n.t("batch1.97c89a4d6630")}</Th>
                                </tr>
                            </thead>
                            <tbody>
                                {logRows.map((row) => (
                                    <tr key={row.id} className="border-t border-border/50">
                                        <Td>{row.date || "-"}</Td>
                                        <Td>{row.employee_name || "-"}</Td>
                                        <Td>{hhmmFromIso(row.shift_start)}</Td>
                                        <Td>{hhmmFromIso(row.clock_in || row.check_in_time)}</Td>
                                        <Td>{hhmmFromIso(row.break_start)}</Td>
                                        <Td>{hhmmFromIso(row.break_end)}</Td>
                                        <Td>{hhmmFromIso(row.clock_out || row.check_out_time)}</Td>
                                        <Td>{hhmmFromIso(row.shift_end)}</Td>
                                        <Td>{hoursLabel(row.regular_hours)}</Td>
                                        <Td>{hoursLabel(row.worked_hours)}</Td>
                                        <Td>{hoursLabel(row.break_hours)}</Td>
                                        <Td>{hoursLabel(row.overtime_hours)}</Td>
                                        <Td>{Number(row.late_minutes || 0)}</Td>
                                        <Td>{statusLabel(row.status)}</Td>
                                        <Td>{statusLabel(row.approval_status || "pending")}</Td>
                                        <Td>{row.gps || "-"}</Td>
                                        <Td>{row.location || row.gps || "-"}</Td>
                                        <Td>{row.photo_verification && row.photo_verification !== "-" ? "Yes" : "-"}</Td>
                                        <Td>{row.device || "-"}</Td>
                                        <Td>{row.completed_field_mode_tasks ?? row.field_tasks_completed ?? "-"}</Td>
                                        <Td>{row.cleaning_records ?? "-"}</Td>
                                        <Td>{row.health_inspections ?? "-"}</Td>
                                        <Td>{row.feeding_records ?? "-"}</Td>
                                        <Td>{row.machinery_inspections ?? row.equipment_used ?? "-"}</Td>
                                        <Td>{row.incident_reports ?? "-"}</Td>
                                        <Td>{row.notes || row.supervisor_notes || "-"}</Td>
                                        <Td>
                                            <div className="flex gap-1">
                                                <Button size="sm" variant="outline" onClick={() => openDetail(row)}>{i18n.t("batch1.69bd4ef9fbd0")}</Button>
                                                <Button
                                                    size="sm"
                                                    variant="outline"
                                                    onClick={() => nav(`/attendance/calendar?employee_id=${encodeURIComponent(row.employee_id)}&year=${sheetYear}&month=${sheetMonth}`)}
                                                >
                                                    {i18n.t("batch1.957f629f801e")}
                                                </Button>
                                            </div>
                                        </Td>
                                    </tr>
                                ))}
                                {logRows.length === 0 && <tr><Td colSpan={30} className="text-center py-8 text-muted-foreground">{i18n.t("batch1.1e62856f241a")}</Td></tr>}
                            </tbody>
                        </table>
                    </Card>
                </TabsContent>

                <TabsContent value="calendar" className="space-y-4">
                    <Card className="p-4">
                        <div className="text-sm text-muted-foreground mb-3">
                            {i18n.t("batch1.3d5c0698ff43")}
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
                            <Input type="number" value={sheetYear} onChange={(e) => setSheetYear(Number(e.target.value || farmNow.year))} className="h-11" />
                            <Input type="number" min="1" max="12" value={sheetMonth} onChange={(e) => setSheetMonth(Number(e.target.value || 1))} className="h-11" />
                            <Button className="h-11" onClick={loadEmployees}>{i18n.t("batch1.6c869993528f")}</Button>
                        </div>
                    </Card>

                    <Card className="p-0 overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead className="bg-secondary/60 text-xs uppercase text-muted-foreground">
                                <tr><Th>{i18n.t("batch1.079711ea16f3")}</Th><Th>{i18n.t("batch1.adac69379a62")}</Th><Th>{i18n.t("batch1.db40106a4051")}</Th><Th>{i18n.t("batch1.a65de8bce998")}</Th><Th>{i18n.t("batch1.97c89a4d6630")}</Th></tr>
                            </thead>
                            <tbody>
                                {employeeRows.map((row) => (
                                    <tr key={row.id} className="border-t border-border/50">
                                        <Td>{row.name || "-"}</Td>
                                        <Td>{row.employee_code || "-"}</Td>
                                        <Td>{row.department || "-"}</Td>
                                        <Td>{row.assigned_areas || "-"}</Td>
                                        <Td>
                                            <Button
                                                size="sm"
                                                variant="outline"
                                                onClick={() => nav(`/attendance/calendar?employee_id=${encodeURIComponent(row.id)}&year=${sheetYear}&month=${sheetMonth}`)}
                                            >
                                                {i18n.t("batch1.7a3a9094b18c")}
                                            </Button>
                                        </Td>
                                    </tr>
                                ))}
                                {employeeRows.length === 0 && <tr><Td colSpan={5} className="text-center py-8 text-muted-foreground">{i18n.t("batch1.c915f1de7864")}</Td></tr>}
                            </tbody>
                        </table>
                    </Card>
                </TabsContent>

                <TabsContent value="timesheets" className="space-y-4">
                    <Card className="p-4">
                        <div className="grid grid-cols-1 md:grid-cols-8 gap-3">
                            <Input type="number" value={sheetYear} onChange={(e) => setSheetYear(Number(e.target.value || farmNow.year))} className="h-11" />
                            <Input type="number" min="1" max="12" value={sheetMonth} onChange={(e) => setSheetMonth(Number(e.target.value || 1))} className="h-11" />
                            <Select value={sheetPeriod} onValueChange={setSheetPeriod}>
                                <SelectTrigger className="h-11"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="daily">{i18n.t("batch1.728298d3dbf4")}</SelectItem>
                                    <SelectItem value="weekly">{i18n.t("batch1.158f3da59275")}</SelectItem>
                                    <SelectItem value="bi-weekly">{i18n.t("batch1.c04cd537976c")}</SelectItem>
                                    <SelectItem value="monthly">{i18n.t("batch1.d31edb7b8a94")}</SelectItem>
                                    <SelectItem value="custom">{i18n.t("batch1.f130609dfc13")}</SelectItem>
                                </SelectContent>
                            </Select>
                            <Input type="date" value={sheetFrom} onChange={(e) => setSheetFrom(e.target.value)} className="h-11" />
                            <Input type="date" value={sheetTo} onChange={(e) => setSheetTo(e.target.value)} className="h-11" />
                            <Button className="h-11" onClick={loadTimesheets}>{i18n.t("batch1.ddcb77ff769e")}</Button>
                            <Button className="h-11" variant="outline" onClick={exportTimesheetExcel}><Download className="w-4 h-4 mr-2" />{i18n.t("batch1.4a245944efb6")}</Button>
                            <Button className="h-11" variant="outline" onClick={exportTimesheetPdf}><Download className="w-4 h-4 mr-2" />{i18n.t("batch1.3dd7d56a8ed3")}</Button>
                            <Button
                                className="h-11"
                                variant="outline"
                                onClick={() => exportRowsCsv((timesheets || []).map((row) => ({
                                    employee: row.employee_name,
                                    employee_code: row.employee_code,
                                    days_present: row.days_present,
                                    days_late: row.days_late,
                                    days_absent: row.days_absent,
                                    regular_hours: row.regular_hours,
                                    overtime_hours: row.overtime_hours,
                                    pending_overtime_hours: row.pending_overtime_hours,
                                    estimated_gross_pay: row.estimated_gross_pay,
                                })), `attendance-timesheet-${sheetYear}-${String(sheetMonth).padStart(2, "0")}.csv`)}
                            >
                                <FileSpreadsheet className="w-4 h-4 mr-2" />{i18n.t("batch1.5755f9ac0aa1")}
                            </Button>
                        </div>
                    </Card>

                    <Card className="p-0 overflow-x-auto" data-testid="timesheet-table">
                        <table className="w-full text-sm">
                            <thead className="bg-secondary/60 text-xs uppercase text-muted-foreground">
                                <tr>
                                    <Th>{i18n.t("batch1.079711ea16f3")}</Th><Th>{i18n.t("batch1.98695f0daeb1")}</Th><Th>{i18n.t("batch1.dc055296f9b5")}</Th><Th>{i18n.t("batch1.c3cb315ce778")}</Th><Th>{i18n.t("batch1.777800dce2f6")}</Th><Th>{i18n.t("batch1.a8231c45a592")}</Th><Th>{i18n.t("batch1.72f1ae653d25")}</Th><Th>{i18n.t("batch1.6210825e9aee")}</Th><Th>{i18n.t("batch1.d79756af148d")}</Th><Th>{i18n.t("batch1.075d521f7323")}</Th><Th>{i18n.t("batch1.8fcb7bac7bd8")}</Th><Th>{i18n.t("batch1.5bc584c57e22")}</Th><Th>{i18n.t("batch1.97c89a4d6630")}</Th>
                                </tr>
                            </thead>
                            <tbody>
                                {timesheets.map((row) => (
                                    <tr key={row.employee_id} className="border-t border-border/50">
                                        <Td>{row.employee_name || "-"}</Td>
                                        <Td>{row.days_present || 0}</Td>
                                        <Td>{row.days_late || 0}</Td>
                                        <Td>{row.days_absent || 0}</Td>
                                        <Td>{hoursLabel(row.regular_hours)}</Td>
                                        <Td>{hoursLabel(row.break_hours)}</Td>
                                        <Td>{hoursLabel(row.overtime_hours)}</Td>
                                        <Td>{hoursLabel(row.pending_overtime_hours)}</Td>
                                        <Td>{formatCurrency(row.overtime_pay || 0, settings?.payroll_currency || "PHP")}</Td>
                                        <Td>{row.approved_days || 0}</Td>
                                        <Td>{row.pending_days || 0}</Td>
                                        <Td>{row.estimated_gross_pay == null ? "Rate not configured" : formatCurrency(row.estimated_gross_pay, settings?.payroll_currency || "PHP")}</Td>
                                        <Td>
                                            <div className="flex gap-1">
                                                <Button
                                                    size="sm"
                                                    variant="outline"
                                                    onClick={() => setTimesheetEntries({ name: row.employee_name, rows: row.entries || [] })}
                                                >{i18n.t("batch1.69bd4ef9fbd0")}</Button>
                                                <Button
                                                    size="sm"
                                                    variant="outline"
                                                    onClick={() => nav(`/attendance/calendar?employee_id=${encodeURIComponent(row.employee_id)}&year=${sheetYear}&month=${sheetMonth}`)}
                                                >
                                                    {i18n.t("batch1.957f629f801e")}
                                                </Button>
                                            </div>
                                        </Td>
                                    </tr>
                                ))}
                                {timesheets.length === 0 && <tr><Td colSpan={13} className="text-center py-8 text-muted-foreground">{i18n.t("batch1.aa9bf183da8a")}</Td></tr>}
                            </tbody>
                        </table>
                    </Card>
                </TabsContent>

                <TabsContent value="overtime" className="space-y-4">
                    <Card className="p-4">
                        <div className="grid grid-cols-1 md:grid-cols-6 gap-3">
                            <Input type="number" value={otYear} onChange={(e) => setOtYear(Number(e.target.value || farmNow.year))} className="h-11" />
                            <Input type="number" min="1" max="12" value={otMonth} onChange={(e) => setOtMonth(Number(e.target.value || 1))} className="h-11" />
                            <Select value={otStatus} onValueChange={setOtStatus}>
                                <SelectTrigger className="h-11"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    {OVERTIME_STATUS.map((st) => <SelectItem key={st} value={st}>{st === "all" ? "All Statuses" : statusLabel(st)}</SelectItem>)}
                                </SelectContent>
                            </Select>
                            <Button className="h-11" onClick={loadOvertime}>{i18n.t("batch1.ddcb77ff769e")}</Button>
                            <Button className="h-11" variant="outline" onClick={exportOvertimeExcel}><FileSpreadsheet className="w-4 h-4 mr-2" />{i18n.t("batch1.4a245944efb6")}</Button>
                        </div>
                        <p className="mt-2 text-xs text-muted-foreground">{i18n.t("batch1.b3d7ace83eec")}</p>
                    </Card>

                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
                        <SummaryCard icon={ShieldCheck} label={i18n.t("batch1.96f608c16cef")} value={overtimeSummary.pending_requests || 0} tone="amber" />
                        <SummaryCard icon={Clock3} label={i18n.t("batch1.41b81eb8db1b")} value={overtimeSummary.approved_this_month || 0} tone="primary" />
                        <SummaryCard icon={Clock3} label={i18n.t("batch1.27eeb7a291bf")} value={overtimeSummary.rejected_this_month || 0} tone="red" />
                        <SummaryCard icon={Clock3} label={i18n.t("batch1.5c3d268b79ed")} value={hoursLabel(overtimeSummary.total_approved_hours || 0)} tone="primary" />
                        <SummaryCard icon={Wallet} label={i18n.t("batch1.5c5c7b61fa6c")} value={formatCurrency(overtimeSummary.estimated_overtime_cost || 0, settings?.payroll_currency || "PHP")} />
                        <SummaryCard icon={Wallet} label={i18n.t("batch1.82b11cd1ea37")} value={formatCurrency(overtimeSummary.paid_overtime_cost || 0, settings?.payroll_currency || "PHP")} />
                    </div>

                    <Card className="p-0 overflow-x-auto" data-testid="overtime-table">
                        <table className="w-full text-sm">
                            <thead className="bg-secondary/60 text-xs uppercase text-muted-foreground">
                                <tr>
                                    <Th>{i18n.t("batch1.eb9a4bc1c0c1")}</Th><Th>{i18n.t("batch1.079711ea16f3")}</Th><Th>{i18n.t("batch1.48a7b68e8254")}</Th><Th>{i18n.t("batch1.d03550660873")}</Th><Th>{i18n.t("batch1.05b1368a3a3a")}</Th><Th>{i18n.t("batch1.bae7d5be7082")}</Th><Th>{i18n.t("batch1.3a9c736b3175")}</Th><Th>{i18n.t("batch1.0764e6ebae1b")}</Th><Th>{i18n.t("batch1.f00f0a0d2a65")}</Th><Th>{i18n.t("batch1.954ea97e382f")}</Th><Th>{i18n.t("batch1.917df91e1730")}</Th><Th>{i18n.t("batch1.f219cc0614ae")}</Th><Th>{i18n.t("batch1.97c89a4d6630")}</Th>
                                </tr>
                            </thead>
                            <tbody>
                                {overtimeRows.map((row) => (
                                    <tr key={row.id} className="border-t border-border/50">
                                        <Td>{row.date || "-"}</Td>
                                        <Td>{row.employee_name || "-"}</Td>
                                        <Td>{row.department || "-"}</Td>
                                        <Td>{hoursLabel(row.requested_overtime_hours)}</Td>
                                        <Td>{hoursLabel(row.approved_overtime_hours)}</Td>
                                        <Td>{statusLabel(row.approval_status || "pending")}</Td>
                                        <Td>{row.overtime_rate == null ? "-" : formatCurrency(row.overtime_rate, settings?.payroll_currency || "PHP")}</Td>
                                        <Td>{row.estimated_overtime_pay == null ? "-" : formatCurrency(row.estimated_overtime_pay, settings?.payroll_currency || "PHP")}</Td>
                                        <Td>{row.requested_at_display || "-"}</Td>
                                        <Td>{row.approved_at_display || "-"}</Td>
                                        <Td>{row.related_subject || "-"}</Td>
                                        <Td>{row.reason || "-"}</Td>
                                        <Td>
                                            <div className="flex gap-1">
                                                <Button
                                                    size="sm"
                                                    variant="outline"
                                                    onClick={() => {
                                                        const entered = window.prompt("Approved overtime hours", String(row.requested_overtime_hours || 0));
                                                        if (entered == null) return;
                                                        const num = Number(entered);
                                                        if (!Number.isFinite(num) || num < 0) {
                                                            toast.error("Enter a valid overtime hour value");
                                                            return;
                                                        }
                                                        const note = window.prompt("Approval note (optional)") || "";
                                                        callOvertimeAction(row.id, "approve", { approved_overtime_hours: num, note });
                                                    }}
                                                    disabled={row.approval_status === "paid"}
                                                >{i18n.t("batch1.7b2c7f146aba")}</Button>
                                                <Button
                                                    size="sm"
                                                    variant="outline"
                                                    onClick={() => {
                                                        const reason = window.prompt("Reject reason");
                                                        if (!reason) return;
                                                        callOvertimeAction(row.id, "reject", { reason });
                                                    }}
                                                    disabled={row.approval_status === "paid"}
                                                >{i18n.t("batch1.2b03b59293b6")}</Button>
                                                <Button
                                                    size="sm"
                                                    variant="outline"
                                                    onClick={() => {
                                                        const entered = window.prompt("Adjusted overtime hours", String(row.approved_overtime_hours || row.requested_overtime_hours || 0));
                                                        if (entered == null) return;
                                                        const num = Number(entered);
                                                        if (!Number.isFinite(num) || num < 0) {
                                                            toast.error("Enter a valid overtime hour value");
                                                            return;
                                                        }
                                                        const reason = window.prompt("Adjustment reason") || "";
                                                        if (!reason.trim()) {
                                                            toast.error("Adjustment reason is required");
                                                            return;
                                                        }
                                                        const note = window.prompt("Adjustment note (optional)") || "";
                                                        callOvertimeAction(row.id, "adjust", { approved_overtime_hours: num, reason, note });
                                                    }}
                                                    disabled={row.approval_status === "paid"}
                                                >{i18n.t("batch1.ab166bd5e8c5")}</Button>
                                                <Button
                                                    size="sm"
                                                    variant="outline"
                                                    onClick={() => {
                                                        const note = window.prompt("Payment note (optional)") || "";
                                                        callOvertimeAction(row.id, "paid", { note });
                                                    }}
                                                    disabled={!(["approved", "adjusted"].includes(String(row.approval_status || "")))}
                                                >{i18n.t("batch1.dc9d4584a554")}</Button>
                                            </div>
                                        </Td>
                                    </tr>
                                ))}
                                {overtimeRows.length === 0 && <tr><Td colSpan={13} className="text-center py-8 text-muted-foreground">{i18n.t("batch1.6ecfc5f3c266")}</Td></tr>}
                            </tbody>
                        </table>
                    </Card>
                </TabsContent>

                <TabsContent value="leaves" className="space-y-4">
                    <Card className="p-4">
                        <div className="grid grid-cols-1 md:grid-cols-8 gap-3">
                            <Input
                                placeholder={i18n.t("batch1.8659d5bb0656")}
                                value={leaveForm.employee_id}
                                onChange={(e) => setLeaveForm((prev) => ({ ...prev, employee_id: e.target.value }))}
                                className="h-11 md:col-span-2"
                            />
                            <Select value={leaveForm.leave_type} onValueChange={(value) => setLeaveForm((prev) => ({ ...prev, leave_type: value }))}>
                                <SelectTrigger className="h-11"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    {LEAVE_TYPES.map((kind) => <SelectItem key={kind} value={kind}>{statusLabel(kind)}</SelectItem>)}
                                </SelectContent>
                            </Select>
                            <Input type="date" value={leaveForm.start_date} onChange={(e) => setLeaveForm((prev) => ({ ...prev, start_date: e.target.value }))} className="h-11" />
                            <Input type="date" value={leaveForm.end_date} onChange={(e) => setLeaveForm((prev) => ({ ...prev, end_date: e.target.value }))} className="h-11" />
                            <Input
                                placeholder={i18n.t("batch1.f219cc0614ae")}
                                value={leaveForm.reason}
                                onChange={(e) => setLeaveForm((prev) => ({ ...prev, reason: e.target.value }))}
                                className="h-11 md:col-span-2"
                            />
                            <Input
                                placeholder={i18n.t("batch1.4d56ca9be0c8")}
                                value={leaveForm.notes}
                                onChange={(e) => setLeaveForm((prev) => ({ ...prev, notes: e.target.value }))}
                                className="h-11"
                            />
                        </div>
                        <div className="mt-3 flex flex-wrap gap-2">
                            <Button className="h-10" onClick={submitLeave}>{leaveEditingId ? "Update Leave" : "Submit Leave"}</Button>
                            {leaveEditingId && (
                                <Button
                                    className="h-10"
                                    variant="outline"
                                    onClick={() => {
                                        setLeaveEditingId("");
                                        setLeaveForm((prev) => ({ ...prev, reason: "", notes: "" }));
                                    }}
                                >{i18n.t("batch1.4d63c1d51940")}</Button>
                            )}
                        </div>
                    </Card>

                    <Card className="p-4">
                        <div className="grid grid-cols-1 md:grid-cols-6 gap-3">
                            <Input type="number" value={leaveYear} onChange={(e) => setLeaveYear(Number(e.target.value || farmNow.year))} className="h-11" />
                            <Select value={leaveStatus} onValueChange={setLeaveStatus}>
                                <SelectTrigger className="h-11"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    {LEAVE_STATUS.map((status) => <SelectItem key={status} value={status}>{status === "all" ? "All Statuses" : statusLabel(status)}</SelectItem>)}
                                </SelectContent>
                            </Select>
                            <Select value={leaveType} onValueChange={setLeaveType}>
                                <SelectTrigger className="h-11"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="all">{i18n.t("batch1.eb672cb3ba02")}</SelectItem>
                                    {LEAVE_TYPES.map((kind) => <SelectItem key={kind} value={kind}>{statusLabel(kind)}</SelectItem>)}
                                </SelectContent>
                            </Select>
                            <Button className="h-11" onClick={loadLeaves}>{i18n.t("batch1.ddcb77ff769e")}</Button>
                        </div>
                    </Card>

                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                        <SummaryCard icon={Users} label={i18n.t("batch1.f7194e6a0d0b")} value={leaveRows.length} />
                        <SummaryCard icon={ShieldCheck} label={i18n.t("batch1.96f608c16cef")} value={leaveRows.filter((row) => String(row.status || "").toLowerCase() === "pending").length} tone="amber" />
                        <SummaryCard icon={Clock3} label={i18n.t("batch1.075d521f7323")} value={leaveRows.filter((row) => String(row.status || "").toLowerCase() === "approved").reduce((sum, row) => sum + Number(row.days || 0), 0)} tone="primary" />
                        <SummaryCard icon={Clock3} label={i18n.t("batch1.c16e1abc1551")} value={(leaveBalance?.balance?.annual ?? "-")} />
                    </div>

                    <Card className="p-0 overflow-x-auto" data-testid="leave-table">
                        <table className="w-full text-sm">
                            <thead className="bg-secondary/60 text-xs uppercase text-muted-foreground">
                                <tr>
                                    <Th>{i18n.t("batch1.079711ea16f3")}</Th><Th>{i18n.t("batch1.3deb74565196")}</Th><Th>{i18n.t("batch1.952f375412e8")}</Th><Th>{i18n.t("batch1.a2bb9d34b8a1")}</Th><Th>{i18n.t("batch1.f6bb0f468a85")}</Th><Th>{i18n.t("batch1.bae7d5be7082")}</Th><Th>{i18n.t("batch1.f219cc0614ae")}</Th><Th>{i18n.t("batch1.97c89a4d6630")}</Th>
                                </tr>
                            </thead>
                            <tbody>
                                {leaveRows.map((row) => (
                                    <tr key={row.id} className="border-t border-border/50">
                                        <Td>{row.employee_name || row.employee_id || "-"}</Td>
                                        <Td>{statusLabel(row.leave_type || "-")}</Td>
                                        <Td>{row.start_date || "-"}</Td>
                                        <Td>{row.end_date || "-"}</Td>
                                        <Td>{Number(row.days || 0)}</Td>
                                        <Td>{statusLabel(row.status || "pending")}</Td>
                                        <Td>{row.reason || "-"}</Td>
                                        <Td>
                                            <div className="flex flex-wrap gap-1">
                                                <Button size="sm" variant="outline" onClick={() => editLeave(row)}>{i18n.t("batch1.5301648dcf6b")}</Button>
                                                <Button
                                                    size="sm"
                                                    variant="outline"
                                                    onClick={() => setLeaveStatusAction(row.id, "approve")}
                                                    disabled={String(row.status || "").toLowerCase() === "approved"}
                                                >{i18n.t("batch1.7b2c7f146aba")}</Button>
                                                <Button
                                                    size="sm"
                                                    variant="outline"
                                                    onClick={() => setLeaveStatusAction(row.id, "reject")}
                                                    disabled={String(row.status || "").toLowerCase() === "rejected"}
                                                >{i18n.t("batch1.2b03b59293b6")}</Button>
                                                <Button size="sm" variant="outline" onClick={() => removeLeave(row.id)}>{i18n.t("batch1.f6fdbe48dc54")}</Button>
                                            </div>
                                        </Td>
                                    </tr>
                                ))}
                                {leaveRows.length === 0 && <tr><Td colSpan={8} className="text-center py-8 text-muted-foreground">{i18n.t("batch1.0c56dbea8b56")}</Td></tr>}
                            </tbody>
                        </table>
                    </Card>
                </TabsContent>

                <TabsContent value="reports" className="space-y-4">
                    <Card className="p-4">
                        <div className="grid grid-cols-1 md:grid-cols-6 gap-3">
                            <Select value={reportType} onValueChange={setReportType}>
                                <SelectTrigger className="h-11"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="attendance_summary">{i18n.t("batch1.8324113fd693")}</SelectItem>
                                    <SelectItem value="employee_report">{i18n.t("batch1.7a7e4f0979e8")}</SelectItem>
                                    <SelectItem value="payroll_report">{i18n.t("batch1.4c53f16bc00d")}</SelectItem>
                                    <SelectItem value="overtime_report">{i18n.t("batch1.4ce5336ea711")}</SelectItem>
                                    <SelectItem value="late_report">{i18n.t("batch1.59f0e720608e")}</SelectItem>
                                    <SelectItem value="leave_report">{i18n.t("batch1.2ddd2d78ecfb")}</SelectItem>
                                    <SelectItem value="absent_report">{i18n.t("batch1.52011cda3e18")}</SelectItem>
                                </SelectContent>
                            </Select>
                            <Input type="date" value={logFrom} onChange={(e) => setLogFrom(e.target.value)} className="h-11" />
                            <Input type="date" value={logTo} onChange={(e) => setLogTo(e.target.value)} className="h-11" />
                            <Button className="h-11" onClick={loadReports}>{i18n.t("batch1.ddcb77ff769e")}</Button>
                            <Button className="h-11" variant="outline" onClick={() => exportLogsExcel()}><FileSpreadsheet className="w-4 h-4 mr-2" />{i18n.t("batch1.a08e11119aef")}</Button>
                            <Button className="h-11" variant="outline" onClick={() => window.print()}><Download className="w-4 h-4 mr-2" />{i18n.t("batch1.5b221e9c2a45")}</Button>
                        </div>
                    </Card>

                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                        <SummaryCard icon={Users} label={i18n.t("batch1.52d0b35277ee")} value={reportSummary.row_count || reportSummary.employee_count || reportRows.length || 0} />
                        <SummaryCard icon={Clock3} label={i18n.t("batch1.6beef1dfb098")} value={hoursLabel(reportSummary.total_regular_hours || 0)} />
                        <SummaryCard icon={Clock3} label={i18n.t("batch1.710ee282158a")} value={hoursLabel(reportSummary.total_overtime_hours || 0)} />
                        <SummaryCard icon={Clock3} label={i18n.t("batch1.06709170d095")} value={reportSummary.total_late_minutes || 0} />
                    </div>

                    <Card className="p-0 overflow-x-auto" data-testid="attendance-reports-table">
                        <table className="w-full text-sm">
                            <thead className="bg-secondary/60 text-xs uppercase text-muted-foreground">
                                <tr>
                                    <Th>{i18n.t("batch1.eb9a4bc1c0c1")}</Th><Th>{i18n.t("batch1.079711ea16f3")}</Th><Th>{i18n.t("batch1.bae7d5be7082")}</Th><Th>{i18n.t("batch1.8cc047ac17d3")}</Th><Th>{i18n.t("batch1.777800dce2f6")}</Th><Th>{i18n.t("batch1.da76a7170f8f")}</Th><Th>{i18n.t("batch1.ba4adca50018")}</Th><Th>{i18n.t("batch1.1776fa32f23e")}</Th><Th>{i18n.t("batch1.a5a74a6df092")}</Th>
                                </tr>
                            </thead>
                            <tbody>
                                {reportRows.map((row, idx) => (
                                    <tr key={row.id || `${row.employee_id || "emp"}-${idx}`} className="border-t border-border/50">
                                        <Td>{row.date || "-"}</Td>
                                        <Td>{row.employee_name || "-"}</Td>
                                        <Td>{statusLabel(row.status || row.pay_type || "-")}</Td>
                                        <Td>{statusLabel(row.approval_status || "-")}</Td>
                                        <Td>{row.regular_hours == null ? "-" : hoursLabel(row.regular_hours)}</Td>
                                        <Td>{row.overtime_hours == null ? "-" : hoursLabel(row.overtime_hours)}</Td>
                                        <Td>{Number(row.late_minutes || 0)}</Td>
                                        <Td>{row.gps || "-"}</Td>
                                        <Td>{row.device || "-"}</Td>
                                    </tr>
                                ))}
                                {reportRows.length === 0 && <tr><Td colSpan={9} className="text-center py-8 text-muted-foreground">{i18n.t("batch1.92928c7b6205")}</Td></tr>}
                            </tbody>
                        </table>
                    </Card>
                </TabsContent>
            </Tabs>

            <Dialog open={detailOpen} onOpenChange={setDetailOpen}>
                <DialogContent className="max-w-3xl">
                    <DialogHeader>
                        <DialogTitle>{i18n.t("batch1.445214c59246")}</DialogTitle>
                        <DialogDescription>{i18n.t("batch1.0755e0e4e2bd")}</DialogDescription>
                    </DialogHeader>
                    {detailRow && (
                        <div className="space-y-4">
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                                <MiniField label={i18n.t("batch1.eb9a4bc1c0c1")} value={detailRow.date} />
                                <MiniField label={i18n.t("batch1.079711ea16f3")} value={detailRow.employee_name} />
                                <MiniField label={i18n.t("batch1.bae7d5be7082")} value={statusLabel(detailRow.status)} />
                                <MiniField label={i18n.t("batch1.8cc047ac17d3")} value={statusLabel(detailRow.approval_status || "pending")} />
                                <MiniField label={i18n.t("batch1.c36f953ce513")} value={hhmmFromIso(detailRow.shift_start)} />
                                <MiniField label={i18n.t("batch1.8d3e921408c8")} value={hhmmFromIso(detailRow.clock_in || detailRow.check_in_time)} />
                                <MiniField label={i18n.t("batch1.31e6ef3947c6")} value={hhmmFromIso(detailRow.break_start)} />
                                <MiniField label={i18n.t("batch1.f81283ceb61e")} value={hhmmFromIso(detailRow.break_end)} />
                                <MiniField label={i18n.t("batch1.7f9bf082bf6a")} value={hhmmFromIso(detailRow.clock_out || detailRow.check_out_time)} />
                                <MiniField label={i18n.t("batch1.b4ebd03d7de2")} value={hhmmFromIso(detailRow.shift_end)} />
                                <MiniField label={i18n.t("batch1.55bd39660c34")} value={hoursLabel(detailRow.worked_hours)} />
                                <MiniField label={i18n.t("batch1.06709170d095")} value={String(detailRow.late_minutes || 0)} />
                            </div>

                            <div className="rounded-lg border p-3 text-xs bg-secondary/20">
                                <div><b>{i18n.t("batch1.46f5a26023da")}</b> {detailRow.manual_override ? "Yes" : "No"}</div>
                                <div><b>{i18n.t("batch1.0e7b9960126c")}</b> {detailRow.override_reason || "-"}</div>
                                <div><b>{i18n.t("batch1.3b088a8d029f")}</b> {detailRow.adjustment_reason || "-"}</div>
                                <div><b>{i18n.t("batch1.9c3befe7e22c")}</b> {detailRow.notes || "-"}</div>
                            </div>

                            <div className="flex flex-wrap gap-2">
                                <Button size="sm" onClick={() => callAttendanceAction(detailRow.id, "approve", { note: "Approved by owner" })}>{i18n.t("batch1.7b2c7f146aba")}</Button>
                                <Button size="sm" variant="outline" onClick={() => {
                                    const reason = window.prompt("Reject reason");
                                    if (!reason) return;
                                    callAttendanceAction(detailRow.id, "reject", { reason });
                                }}>{i18n.t("batch1.2b03b59293b6")}</Button>
                                <Button size="sm" variant="outline" onClick={() => openAdjust(detailRow)}>{i18n.t("batch1.ab166bd5e8c5")}</Button>
                                <Button size="sm" variant="outline" onClick={() => {
                                    const reason = window.prompt("Lock reason (optional)") || "";
                                    callAttendanceAction(detailRow.id, "lock", { reason });
                                }}>{i18n.t("batch1.891ebccd5baa")}</Button>
                            </div>
                        </div>
                    )}
                </DialogContent>
            </Dialog>

            <Dialog open={adjustOpen} onOpenChange={setAdjustOpen}>
                <DialogContent className="max-w-3xl">
                    <DialogHeader>
                        <DialogTitle>{i18n.t("batch1.69064d7e30fe")}</DialogTitle>
                        <DialogDescription>{i18n.t("batch1.5401ccefc6f1")}</DialogDescription>
                    </DialogHeader>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <div><Label className="tiny-label">{i18n.t("batch1.9abe23c2289e")}</Label><Input value={adjustForm.clock_in} onChange={(e) => setAdjustForm({ ...adjustForm, clock_in: e.target.value })} className="h-10 mt-2" /></div>
                        <div><Label className="tiny-label">{i18n.t("batch1.b49e92dddae0")}</Label><Input value={adjustForm.break_start} onChange={(e) => setAdjustForm({ ...adjustForm, break_start: e.target.value })} className="h-10 mt-2" /></div>
                        <div><Label className="tiny-label">{i18n.t("batch1.27d9f7d0f448")}</Label><Input value={adjustForm.break_end} onChange={(e) => setAdjustForm({ ...adjustForm, break_end: e.target.value })} className="h-10 mt-2" /></div>
                        <div><Label className="tiny-label">{i18n.t("batch1.5beab2dda050")}</Label><Input value={adjustForm.clock_out} onChange={(e) => setAdjustForm({ ...adjustForm, clock_out: e.target.value })} className="h-10 mt-2" /></div>
                        <div><Label className="tiny-label">{i18n.t("batch1.8e6ff8d90e17")}</Label><Input value={adjustForm.shift_end} onChange={(e) => setAdjustForm({ ...adjustForm, shift_end: e.target.value })} className="h-10 mt-2" /></div>
                        <div><Label className="tiny-label">{i18n.t("batch1.bae7d5be7082")}</Label><Input value={adjustForm.status} onChange={(e) => setAdjustForm({ ...adjustForm, status: e.target.value })} className="h-10 mt-2" placeholder={i18n.t("batch1.a3d0e5f9ad0c")} /></div>
                        <div className="md:col-span-2"><Label className="tiny-label">{i18n.t("batch1.70440046a3dc")}</Label><Input value={adjustForm.notes} onChange={(e) => setAdjustForm({ ...adjustForm, notes: e.target.value })} className="h-10 mt-2" /></div>
                        <div className="md:col-span-2"><Label className="tiny-label">{i18n.t("batch1.c530d39862c2")}</Label><Input value={adjustForm.reason} onChange={(e) => setAdjustForm({ ...adjustForm, reason: e.target.value })} className="h-10 mt-2" /></div>
                    </div>
                    <Button className="h-11" onClick={saveAdjust}>{i18n.t("batch1.174a464e2022")}</Button>
                </DialogContent>
            </Dialog>

            <Dialog open={!!timesheetEntries} onOpenChange={(open) => !open && setTimesheetEntries(null)}>
                <DialogContent className="max-w-4xl">
                    <DialogHeader>
                        <DialogTitle>{timesheetEntries?.name || "Employee"} {i18n.t("batch1.44445057411b")}</DialogTitle>
                        <DialogDescription>{i18n.t("batch1.deaa3bd8c746")}</DialogDescription>
                    </DialogHeader>
                    <div className="max-h-[60vh] overflow-y-auto border rounded-lg">
                        <table className="w-full text-sm">
                            <thead className="bg-secondary/60 text-xs uppercase text-muted-foreground sticky top-0">
                                <tr><Th>{i18n.t("batch1.eb9a4bc1c0c1")}</Th><Th>{i18n.t("batch1.bae7d5be7082")}</Th><Th>{i18n.t("batch1.777800dce2f6")}</Th><Th>{i18n.t("batch1.a8231c45a592")}</Th><Th>{i18n.t("batch1.da76a7170f8f")}</Th><Th>{i18n.t("batch1.ba4adca50018")}</Th><Th>{i18n.t("batch1.8cc047ac17d3")}</Th></tr>
                            </thead>
                            <tbody>
                                {(timesheetEntries?.rows || []).map((row) => (
                                    <tr key={row.id} className="border-t border-border/50">
                                        <Td>{row.date}</Td>
                                        <Td>{statusLabel(row.status)}</Td>
                                        <Td>{hoursLabel(row.regular_hours)}</Td>
                                        <Td>{hoursLabel(row.break_hours)}</Td>
                                        <Td>{hoursLabel(row.overtime_hours)}</Td>
                                        <Td>{Number(row.late_minutes || 0)}</Td>
                                        <Td>{statusLabel(row.approval_status || "pending")}</Td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </DialogContent>
            </Dialog>
        </div>
    );
}

function SummaryCard({ icon: Icon, label, value, tone = "slate" }) {
    const cls = tone === "primary" ? "bg-primary/5 border-primary/20" : tone === "amber" ? "bg-amber-500/5 border-amber-500/20" : tone === "red" ? "bg-destructive/5 border-destructive/20" : "bg-secondary border-border";
    return (
        <Card className={`p-3 border ${cls}`}>
            <div className="flex items-center gap-2 text-xs text-muted-foreground"><Icon className="w-3.5 h-3.5" />{label}</div>
            <div className="text-2xl font-bold mt-1">{value}</div>
        </Card>
    );
}

function Th({ children, className = "" }) {
    return <th className={`px-3 py-2 text-left whitespace-nowrap ${className}`}>{children}</th>;
}

function Td({ children, className = "", colSpan }) {
    return <td colSpan={colSpan} className={`px-3 py-2 whitespace-nowrap ${className}`}>{children}</td>;
}

function MiniField({ label, value }) {
    return (
        <div className="rounded-lg border p-2">
            <div className="tiny-label">{label}</div>
            <div className="text-sm mt-1">{value || "-"}</div>
        </div>
    );
}
